// Unit ids follow Enums.unitOptions: 1 g, 2 kg, 3 L, 4 ml, 5 unidades, 6 bochas.
const BASE = {
  1: { group: 'mass', toBase: 1 },
  2: { group: 'mass', toBase: 1000 },
  3: { group: 'volume', toBase: 1000 },
  4: { group: 'volume', toBase: 1 },
  5: { group: 'count', toBase: 1 },
  6: { group: 'bochas', toBase: 1 },
};

/** True when converting needs the supply's density (one side is mass, the other volume). */
export const bridgesMassAndVolume = (from, to) => {
  const groups = [BASE[from]?.group, BASE[to]?.group];
  return groups.includes('mass') && groups.includes('volume');
};

/**
 * Quantity expressed in `to` for `quantity` of `from`; null when the units cannot be converted.
 * `density` is the supply's conversion factor (grams per ml): it bridges mass <-> volume.
 */
export const convertQuantity = (quantity, from, to, density) => {
  const source = BASE[from];
  const target = BASE[to];
  if (!source || !target) return null;
  const base = Number(quantity) * source.toBase;
  if (source.group === target.group) return base / target.toBase;
  const bridge = Number(density);
  if (!(bridge > 0)) return null;
  if (source.group === 'volume' && target.group === 'mass') return (base * bridge) / target.toBase;
  if (source.group === 'mass' && target.group === 'volume') return (base / bridge) / target.toBase;
  return null;
};

const UNIT_WORDS = [
  [/^(kg|kgs|kilo|kilos|kilogramos?)$/, 2],
  [/^(g|gr|grs|gramos?)$/, 1],
  [/^(l|lt|lts|litros?)$/, 3],
  [/^(ml|cc|cm3|mililitros?)$/, 4],
  [/^(un|u|unid|unids|unidad|unidades)$/, 5],
];

const unitFromWord = (word) => (UNIT_WORDS.find(([pattern]) => pattern.test(word.toLowerCase())) || [])[1] || null;
const toNumber = (text) => Number(String(text).replace(',', '.'));

const UNIT_PATTERN = '(kg|kgs|kilos?|kilogramos?|gramos?|grs?|g|litros?|lts?|l|ml|cc|cm3|unidades|unidad|unids?|un|u)';
const NUMBER_PATTERN = '(\\d+(?:[.,]\\d+)?)';

/**
 * Best-effort read of the pack size in a supplier description: "Harina 000 25kg", "Aceite 900 ml",
 * "Leche 12 x 1 L" (= 12 L), "Servilletas x 500". Returns { quantity, unit } or null.
 * It is only a suggestion: the user can always correct it.
 */
export const detectPackFromDescription = (description) => {
  const text = String(description || '');
  const multiplied = new RegExp(`(?:^|[^\\d])(\\d+)\\s*[xX]\\s*${NUMBER_PATTERN}\\s*${UNIT_PATTERN}(?![a-zA-Z])`, 'i').exec(text);
  if (multiplied) {
    const unit = unitFromWord(multiplied[3]);
    if (unit) return { quantity: toNumber(multiplied[1]) * toNumber(multiplied[2]), unit };
  }
  const measured = new RegExp(`(?:^|[^\\d.,])${NUMBER_PATTERN}\\s*${UNIT_PATTERN}(?![a-zA-Z])`, 'i').exec(text);
  if (measured) {
    const unit = unitFromWord(measured[2]);
    if (unit) return { quantity: toNumber(measured[1]), unit };
  }
  const count = /[xX]\s*(\d+)(?![\d.,]*\s*[a-zA-Z])/.exec(text);
  if (count) return { quantity: Number(count[1]), unit: 5 };
  return null;
};

/** Default origin for a freshly chosen supply: the detected pack when it converts to the supply unit, else 1 supply unit. */
export const defaultOrigin = (description, supplyUnit, density) => {
  const detected = detectPackFromDescription(description);
  if (detected && convertQuantity(detected.quantity, detected.unit, supplyUnit, density) != null) {
    return { originQty: detected.quantity, originUnit: detected.unit, detected: true };
  }
  return { originQty: 1, originUnit: supplyUnit, detected: false };
};
